/**
 * Пилюли состояния и пилюля-действие рядом с ними.
 *
 * Общий вид для счёта и для заказа: «Отправлен / Не отправлен», «Оплачен /
 * Не оплачен» и кнопка, которая это состояние меняет. Раньше в счёте они были
 * свои, в заказе — своя прямоугольная кнопка-переключатель, и одно и то же
 * («деньги пришли») выглядело в двух местах по-разному.
 *
 * Цвет метки говорит о ПОЛОЖЕНИИ ДЕЛ, а не о действии: «Не оплачен» —
 * красным, потому что это долг, а не нейтральное состояние; частичная
 * оплата — жёлтым; «Оплачен» — зелёным.
 *
 * Кнопка при этом БЕЛАЯ. Цветной она спорила с меткой рядом: два ярких пятна
 * в одном ряду, и непонятно, где состояние, а где действие. Белая с тонкой
 * рамкой — как «Сводный отчёт» и «Прайс-лист» в шапках разделов: видно, что
 * на неё нажимают, но она не кричит.
 */

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface Метка {
  color: string;
  icon: LucideIcon;
  label: string;
  /** Пояснение под названием. Без него пилюля однострочная. */
  hint?: string;
}

/** Одна метка состояния. Форма общая, чтобы соседние метки читались парой. */
export function Pilule({ look }: { look: Метка }) {
  const Icon = look.icon;
  return (
    <span
      className="inline-flex items-center gap-2.5 rounded-full py-1.5 pl-3 pr-4"
      style={{
        background: `color-mix(in srgb, ${look.color} 12%, transparent)`,
        border: `1px solid color-mix(in srgb, ${look.color} 35%, transparent)`,
      }}
    >
      <Icon size={16} style={{ color: look.color }} />
      <span className="leading-tight">
        <span className="block text-sm font-medium" style={{ color: look.color }}>
          {look.label}
        </span>
        {look.hint && <span className="block text-[11px] text-ink2">{look.hint}</span>}
      </span>
    </span>
  );
}

/** Пилюля-ДЕЙСТВИЕ: та же форма, но белая и нажимается. */
export function ActionPill({
  icon: Icon,
  onClick,
  title,
  disabled = false,
  children,
}: {
  icon: LucideIcon;
  onClick: () => void;
  title?: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="inline-flex items-center gap-2 rounded-full border py-1.5 pl-3 pr-4 text-sm font-medium text-ink transition-colors hover:bg-black/5 disabled:opacity-50"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      <Icon size={16} className="text-ink2" />
      {children}
    </button>
  );
}
